import { findElements, getData } from 'dom-helper';
import GwiApi from 'gwi-api';
import BrickConfigClassMap from '../utils/brick_config_class_map';
import BuilderMap from '../utils/builder_map';
import Logger from '../utils/logger';
import Brick from './brick';
import PosterBrickConfig from './poster-brick-config';
import WidgetV2BrickConfig from './widget-v2-brick-config';
import { BRICK_INCLUDE_DIV_CLASS_NAME, WATCHABLE_TYPES } from '../config/constants';
import ENV from '../config/env';
import AbstractWatchableBrickConfig from './abstract-watchable-brick-config';

export default class Mason {
  static async build(el, brickConfig) {
    if (brickConfig instanceof AbstractWatchableBrickConfig && brickConfig.isRequiresSearch()) {
      const searchLog = brickConfig.searchYear ? `${brickConfig.search} (${brickConfig.searchYear})` : brickConfig.search;
      Logger.debug(`Brick Config is set with the search parameter. Searching for term '${searchLog}'`);

      const newBrickConfig = await Mason.searchForWatchable(brickConfig);
      return Mason.build(el, newBrickConfig);
    }

    if (!brickConfig.isValid()) {
      Logger.error('Brick config is not valid:', brickConfig.errors().join(', '));
      return null;
    }

    const name = Mason.generateName();
    brickConfig.setId(name);

    if (brickConfig.isIframe()) {
      return Mason.buildWithIframe(el, brickConfig);
    }

    return Mason.buildOnPage(el, brickConfig);
  }

  static async searchForWatchable(brickConfig) {
    const api = new GwiApi(brickConfig.apiKey, null, ENV.gwiHost);
    const options = brickConfig.searchYear ? { year: brickConfig.searchYear } : {};

    let response;
    let watchable;
    let watchableType;
    if (brickConfig.isMovie()) {
      response = await api.searchMovies(brickConfig.search, options);

      watchable = response.search.movies[0];
      watchableType = WATCHABLE_TYPES.MOVIE;
    } else if (brickConfig.isShow()) {
      response = await api.searchShows(brickConfig.search, options);

      watchable = response.search.shows[0];
      watchableType = WATCHABLE_TYPES.SHOW;
    } else {
      response = await api.search(brickConfig.search);

      if (response.search.movies.length !== 0) {
        watchable = response.search.movies[0];
        watchableType = WATCHABLE_TYPES.MOVIE;
      } else {
        watchable = response.search.shows[0];
        watchableType = WATCHABLE_TYPES.SHOW;
      }
    }

    const watchableId = watchable ? watchable.id : null;
    const searchLogMessage = brickConfig.searchYear ? `${brickConfig.search} (${brickConfig.searchYear})` : brickConfig.search;

    if (!watchableType || !watchableId) {
      Logger.warn(`Search for '${searchLogMessage}' was unsuccessful. Returning no results.`);
    } else {
      Logger.debug(`Search for '${searchLogMessage}' was successful. Rendering with watchableType=${watchableType} and watchableId=${watchableId}`);
    }

    brickConfig.setWatchable(watchableType, watchableId);
    brickConfig.resetSearch();

    return brickConfig;
  }

  static async buildWithIframe(el, brickConfig) {
    const parentNode = el.parentNode;
    const builder = BuilderMap.get(brickConfig.brickType);
    const iframeHolder = await builder.build(brickConfig.id, brickConfig);

    const iframe = iframeHolder.querySelectorAll('iframe')[0];

    parentNode.replaceChild(iframeHolder, el);

    return new Brick(iframe, brickConfig);
  }

  static buildOnPage(el, brickConfig) {
    window.BrickLayer.queue('build', el, brickConfig.toQueryParamObject());

    return new Brick(el, brickConfig);
  }

  static async initializeHTMLBricks(defaultParameters) {
    // For some reason querySelectorAll does not have a `map` function.
    // A NodeList in Firefox also does not have a forEach method. Convert it
    // to an array first.

    // 1. Iterate through gwi-brick elements and create brickConfigs for each.
    const brickElements = findElements(`.${BRICK_INCLUDE_DIV_CLASS_NAME}`);

    Logger.debug('Iterating through', brickElements.length, 'brick elements...');
    if (brickElements.length === 0) {
      Logger.debug('No elements found. Returning.');
      return new Promise(resolve => resolve([]));
    }

    const brickConfigs = await Promise.all(brickElements.map(async (element) => {
      const data = getData(element);
      const BrickConfigClass = BrickConfigClassMap.get(data.brickType);
      const brickConfig = new BrickConfigClass({ ...data, ...defaultParameters });

      const useLatestEpisode = brickConfig instanceof WidgetV2BrickConfig
        && brickConfig.isShow()
        && brickConfig.isLatestEpisode();

      if (useLatestEpisode) {
        const api = new GwiApi(brickConfig.apiKey, null, ENV.gwiHost);
        const response = await api.getLatestAvailableEpisode(brickConfig.watchableId);
        const latestAvailableEpisode = response.episode;

        if (latestAvailableEpisode) {
          brickConfig.watchableId = latestAvailableEpisode.id;
          brickConfig.watchableType = 'episode';
        }
      }
          // TODO: Should I make a totally new object that contains the element?
      brickConfig.element = element;

      return brickConfig;
    }));

    const bricksThatNeedAPI = brickConfigs.filter(config => config instanceof PosterBrickConfig);
    const bricksThatDoNotNeedAPI = brickConfigs.filter(
      config => !(config instanceof PosterBrickConfig),
    );

    return this.enrichApiBricks(bricksThatNeedAPI, defaultParameters).then(
      enrichedBricks => enrichedBricks.concat(bricksThatDoNotNeedAPI),
    );
  }

  static enrichApiBricks(brickConfigs, defaultParameters) {
    const { apiKey } = defaultParameters;

    // 2. Throw out any of them that are invalid.
    const validBrickConfigs = brickConfigs.filter(config => config.isValid());
    if (validBrickConfigs.length === 0) {
      Logger.debug('There are no valid brick configs. Exiting.');
      // We still want to return the invalid brick configs so that the
      // errors are still displayed to the user.
      return new Promise(resolve => resolve(brickConfigs));
    }

    // 3. Fetch all brick configs in one endpoint.
    const api = new GwiApi(apiKey, null, ENV.gwiHost);

    return api.getBricksForBrickData(validBrickConfigs).then((responseJSON) => {
      Logger.debug('responseJSON', responseJSON);
      const apiBricks = responseJSON.bricks.map((brick) => {
        const BrickConfigClass = BrickConfigClassMap.get('poster');
        return new BrickConfigClass({ ...brick, ...defaultParameters });
      });

      // TODO: This is technically O(n^2). So should fix this sooner rather than later.
      // 4. Iterate through all of the gwi-brick elements again.
      const brickDataArray = brickConfigs.map((elementBrickData) => {
        const { watchableType, watchableId } = elementBrickData;

        // 5. Find the corresponding brick returned from the API.
        const apiBrickData = apiBricks.filter(brick =>
          brick.watchableType === watchableType && brick.watchableId === watchableId,
        )[0];

        // 6. Merge data that is on the element's brick data with what is returned.
        // This allows for annotation/poster/etc. to be overridden.
        if (apiBrickData) {
          elementBrickData.merge(apiBrickData);
        }

        return elementBrickData;
      });

      return brickDataArray;
    });
  }

  static generateName() {
    const NAME_PREFIX = 'gwi-brick-';
    const uniqueId = Mason.generateUniqueId();

    return `${NAME_PREFIX}${uniqueId}`;
  }

  static generateUniqueId() {
    return Math.floor(Date.now() * Math.random());
  }
}



// WEBPACK FOOTER //
// ./src/models/mason.js